import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { companyInfo } from '../data/mock';
import { motion } from 'framer-motion';
import { FadeInWhenVisible, StaggerContainer, StaggerItem, CinematicCard } from '../components/CinematicEffects';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simular envío de formulario
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }, 3000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Dirección',
      content: companyInfo.address,
      link: `https://www.google.com/maps?q=${companyInfo.coordinates.lat},${companyInfo.coordinates.lng}`
    },
    {
      icon: Phone,
      title: 'Teléfono',
      content: companyInfo.phone,
      link: `tel:${companyInfo.phone}`
    },
    {
      icon: Mail,
      title: 'Correo Electrónico',
      content: companyInfo.email,
      link: `mailto:${companyInfo.email}`
    },
    {
      icon: Clock,
      title: 'Horario de Atención',
      content: companyInfo.hours,
      link: null
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Contáctanos
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Estamos aquí para ayudarte. Comunícate con nosotros por cualquiera de estos medios
            </p>
          </motion.div>
        </div>
      </section>

      {/* Información de Contacto */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <StaggerContainer>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <StaggerItem key={index}>
                    <CinematicCard className="border-0 shadow-xl text-center h-full">
                      <CardContent className="p-8">
                        <motion.div
                          whileHover={{ scale: 1.1, rotate: 10 }}
                          transition={{ duration: 0.3 }}
                          className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl mb-6"
                        >
                          <Icon className="w-8 h-8 text-white" />
                        </motion.div>
                        <h3 className="text-lg font-bold text-gray-900 mb-3">{info.title}</h3>
                        {info.link ? (
                          <a
                            href={info.link}
                            className="text-gray-600 hover:text-blue-900 transition-colors text-sm"
                            target={info.icon === MapPin ? "_blank" : undefined}
                            rel={info.icon === MapPin ? "noopener noreferrer" : undefined}
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-gray-600 text-sm">{info.content}</p>
                        )}
                      </CardContent>
                    </CinematicCard>
                  </StaggerItem>
                );
              })}
            </div>
          </StaggerContainer>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Formulario de Contacto */}
            <Card className="border-0 shadow-2xl">
              <CardContent className="p-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Envíanos un Mensaje
                </h2>
                <p className="text-gray-600 mb-8">
                  Completa el formulario y te responderemos lo antes posible
                </p>

                {submitted ? (
                  <div className="bg-green-50 border-2 border-green-500 rounded-lg p-8 text-center">
                    <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-green-900 mb-2">
                      ¡Mensaje Enviado!
                    </h3>
                    <p className="text-green-700">
                      Gracias por contactarnos. Responderemos tu mensaje pronto.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-gray-700 font-semibold">
                        Nombre Completo *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Tu nombre"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email" className="text-gray-700 font-semibold">
                          Correo Electrónico *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="tu@email.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="phone" className="text-gray-700 font-semibold">
                          Teléfono
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="(951) 000-0000"
                          value={formData.phone}
                          onChange={handleChange}
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-gray-700 font-semibold">
                        Asunto *
                      </Label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Selecciona un asunto</option>
                        <option value="reserva">Consulta sobre Reservas</option>
                        <option value="cambio">Cambio o Cancelación</option>
                        <option value="equipaje">Información sobre Equipaje</option>
                        <option value="reclamo">Reclamo o Sugerencia</option>
                        <option value="otro">Otro</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-gray-700 font-semibold">
                        Mensaje *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Escribe tu mensaje aquí..."
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="mt-2"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 text-white text-lg py-6"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Enviar Mensaje
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* Mapa */}
            <div className="space-y-8">
              <Card className="border-0 shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-br from-blue-900 to-blue-700 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Visítanos</h3>
                  <p className="text-blue-100">
                    Encuentra nuestra oficina en el corazón de Oaxaca
                  </p>
                </div>
                <div className="h-96">
                  <iframe
                    src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3814.5!2d${companyInfo.coordinates.lng}!3d${companyInfo.coordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTfCsDA0JzE1LjAiTiA5NsKwNDMnMDAuMCJX!5e0!3m2!1sen!2smx!4v1234567890`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Ubicación Aerotucán"
                  ></iframe>
                </div>
              </Card>

              {/* Información Adicional */}
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    ¿Prefieres llamar?
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Nuestro equipo de atención al cliente está disponible para ayudarte 
                    de lunes a sábado durante nuestro horario de oficina.
                  </p>
                  <a href={`tel:${companyInfo.phone}`}>
                    <Button
                      size="lg"
                      className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Llamar Ahora: {companyInfo.phone}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            ¿Listo para Volar?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Encuentra y reserva tu próximo vuelo en minutos
          </p>
          <a href="/rutas">
            <Button
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-10 py-6"
            >
              Buscar Vuelos
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
